/**
 * Spring physics primitive — the numeric heart of the motion system.
 *
 * A critically-damped-ish mass/spring/damper integrated with semi-implicit
 * Euler at a fixed internal step (stable regardless of display refresh rate).
 * Consumers receive a plain number per frame and are responsible for writing
 * it to a GPU-friendly property (`transform` / `opacity`) — never layout.
 *
 * Two layers:
 *   - `createSpringSimulator` / `springFrames`: pure math, unit-testable.
 *   - `animateSpring` / `createRetargetableSpring`: rAF drivers with a
 *     prefers-reduced-motion guard (jump to target, no animation).
 */

import { prefersReducedMotion } from "./reduced-motion";

export interface SpringConfig {
  /** Spring constant — higher = faster/snappier. */
  stiffness: number;
  /** Friction — higher = less oscillation. */
  damping: number;
  /** Attached mass — higher = more sluggish/weighty. */
  mass?: number;
  /** Rest thresholds: both must be under these to settle. */
  restDelta?: number;
  restSpeed?: number;
}

/** Named presets tuned for a native-app feel at 60fps. */
export const SPRINGS = {
  /** Press-in of a touched control: immediate, no wobble. */
  press: { stiffness: 900, damping: 40 },
  /** Release of a touched control: fast with a whisper of overshoot. */
  release: { stiffness: 550, damping: 22 },
  /** Playful pop (like, badge, celebration): visible overshoot. */
  pop: { stiffness: 480, damping: 13 },
  /** Content settle (cards, sections): smooth, no overshoot. */
  gentle: { stiffness: 190, damping: 26 },
  /** Bottom sheet / drawer settle: weighty but controlled. */
  sheet: { stiffness: 300, damping: 30 },
} as const satisfies Record<string, SpringConfig>;

const DEFAULT_MASS = 1;
const DEFAULT_REST_DELTA = 0.001;
const DEFAULT_REST_SPEED = 0.001;
/** Fixed integration step (ms). Decoupled from the display's frame rate. */
const STEP_MS = 1000 / 120;
/** Hard cap so a mis-tuned spring can never loop forever (~5s at 120Hz). */
const MAX_STEPS = 600 * 5;

interface SpringState {
  value: number;
  velocity: number;
  done: boolean;
}

/**
 * Pure simulator: returns a stepper that advances the spring by `dtMs` and
 * reports the new state. Semi-implicit Euler with internal fixed sub-steps —
 * deterministic and stable for stiff springs.
 */
export function createSpringSimulator(
  from: number,
  to: number,
  config: SpringConfig,
  initialVelocity = 0,
): (dtMs: number) => SpringState {
  const mass = config.mass ?? DEFAULT_MASS;
  const restDelta = config.restDelta ?? DEFAULT_REST_DELTA;
  const restSpeed = config.restSpeed ?? DEFAULT_REST_SPEED;

  let value = from;
  let velocity = initialVelocity;
  let done = from === to && Math.abs(initialVelocity) < restSpeed;
  let stepsTaken = 0;

  return (dtMs: number): SpringState => {
    if (done) {
      return { value: to, velocity: 0, done: true };
    }
    let remaining = Math.min(dtMs, 64); // clamp long frames (tab was hidden)
    while (remaining > 0 && !done) {
      const dt = Math.min(STEP_MS, remaining) / 1000;
      const springForce = -config.stiffness * (value - to);
      const dampingForce = -config.damping * velocity;
      const acceleration = (springForce + dampingForce) / mass;
      velocity += acceleration * dt;
      value += velocity * dt;
      remaining -= STEP_MS;
      stepsTaken += 1;

      const scale = Math.max(Math.abs(to - from), 1);
      if (
        (Math.abs(value - to) < restDelta * scale && Math.abs(velocity) < restSpeed * scale) ||
        stepsTaken >= MAX_STEPS
      ) {
        value = to;
        velocity = 0;
        done = true;
      }
    }
    return { value, velocity, done };
  };
}

/**
 * Full trajectory sampled at ~60fps. Test/debug helper — production code uses
 * the rAF drivers below.
 */
export function springFrames(
  from: number,
  to: number,
  config: SpringConfig,
  frameMs = 1000 / 60,
): number[] {
  const step = createSpringSimulator(from, to, config);
  const frames: number[] = [];
  for (let i = 0; i < MAX_STEPS; i += 1) {
    const s = step(frameMs);
    frames.push(s.value);
    if (s.done) {
      break;
    }
  }
  return frames;
}

interface AnimateSpringOptions {
  from: number;
  to: number;
  config?: SpringConfig;
  velocity?: number;
  /** Called once per animation frame with the current value. */
  onUpdate: (value: number) => void;
  onComplete?: () => void;
}

/**
 * One-shot rAF-driven spring. Respects prefers-reduced-motion by jumping to
 * the target immediately. Returns a cancel function.
 */
export function animateSpring(options: AnimateSpringOptions): () => void {
  const config = options.config ?? SPRINGS.gentle;

  if (prefersReducedMotion() || typeof requestAnimationFrame !== "function") {
    options.onUpdate(options.to);
    options.onComplete?.();
    return () => {};
  }

  const step = createSpringSimulator(options.from, options.to, config, options.velocity ?? 0);
  let last = performance.now();
  let raf = 0;
  let cancelled = false;

  const frame = (now: number) => {
    if (cancelled) {
      return;
    }
    const state = step(now - last);
    last = now;
    options.onUpdate(state.value);
    if (state.done) {
      options.onComplete?.();
      return;
    }
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  return () => {
    cancelled = true;
    cancelAnimationFrame(raf);
  };
}

interface RetargetableSpring {
  /** Redirect the spring toward a new target, preserving momentum. */
  setTarget: (target: number, config?: SpringConfig) => void;
  /** Current animated value. */
  value: () => number;
  /** Stop the loop and release resources. */
  dispose: () => void;
}

/**
 * Persistent spring that can be re-targeted mid-flight (press → release keeps
 * the current velocity, exactly like a native touch highlight). Runs a rAF
 * loop only while out of rest. Reduced motion → jumps instantly.
 */
export function createRetargetableSpring(
  initial: number,
  onUpdate: (value: number) => void,
): RetargetableSpring {
  let value = initial;
  let velocity = 0;
  let target = initial;
  let config: SpringConfig = SPRINGS.release;
  let raf = 0;
  let running = false;
  let disposed = false;
  let last = 0;

  const loop = (now: number) => {
    if (disposed) {
      return;
    }
    const step = createSpringSimulator(value, target, config, velocity);
    const state = step(now - last);
    last = now;
    value = state.value;
    velocity = state.velocity;
    onUpdate(value);
    if (state.done) {
      running = false;
      return;
    }
    raf = requestAnimationFrame(loop);
  };

  return {
    setTarget(nextTarget, nextConfig) {
      target = nextTarget;
      if (nextConfig) {
        config = nextConfig;
      }
      if (prefersReducedMotion() || typeof requestAnimationFrame !== "function") {
        value = nextTarget;
        velocity = 0;
        onUpdate(value);
        return;
      }
      if (!running) {
        running = true;
        last = performance.now();
        raf = requestAnimationFrame(loop);
      }
    },
    value: () => value,
    dispose() {
      disposed = true;
      running = false;
      cancelAnimationFrame(raf);
    },
  };
}
